import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { defaultSeo } from "./content";

const ITRK_SCRIPT_SRC = "https://www.it-recht-kanzlei.de/js/itrk-legaltext.js";
const LOGO_URL = "/assets/magnetixs-wordmark-white.png";

// Lädt das IT-Recht-Kanzlei-Snippet bei jedem Seitenaufruf frisch neu,
// damit der Rechtstext immer aktuell vom Kanzlei-Server gezogen wird.
const ItrkLegalText = ({ snippetUrl }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!snippetUrl || !containerRef.current) return;
    const script = document.createElement("script");
    script.src = ITRK_SCRIPT_SRC;
    script.async = true;
    containerRef.current.appendChild(script);
    return () => {
      script.remove();
    };
  }, [snippetUrl]);

  if (!snippetUrl) {
    return (
      <p className="text-[#A3A3A0] text-base" data-testid="legal-text-placeholder">
        Dieser Rechtstext wird in Kürze hinzugefügt.
      </p>
    );
  }

  return (
    <div ref={containerRef} data-testid="legal-text-content">
      <div className="itrk-legaltext" data-itrk-legaltext-url={snippetUrl}></div>
    </div>
  );
};

export const LegalPage = ({ title, snippetUrl, path }) => {
  useEffect(() => {
    document.title = `${title} | magnetixs`;
    const canonical = document.querySelector('link[rel="canonical"]');
    const ogUrl = document.querySelector('meta[property="og:url"]');
    const url = `https://www.magnetixs.de${path}`;
    if (canonical) canonical.setAttribute("href", url);
    if (ogUrl) ogUrl.setAttribute("content", url);

    return () => {
      document.title = defaultSeo.title;
      if (canonical) canonical.setAttribute("href", "https://www.magnetixs.de/");
      if (ogUrl) ogUrl.setAttribute("content", "https://www.magnetixs.de/");
    };
  }, [title, path]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F3F3F0]" data-testid="legal-page">
      <header className="bg-[#0A0A0A] border-b border-white/10">
        <div className="max-w-3xl mx-auto px-5 sm:px-10 py-5 flex items-center justify-between">
          <Link to="/" data-testid="legal-page-logo-link">
            <img src={LOGO_URL} alt="Magnetixs" className="h-7 object-contain" />
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-sm font-semibold text-[#C9C9CC] hover:text-white"
            data-testid="legal-page-back-link"
          >
            <ArrowLeft size={16} /> Zurück zum Shop
          </Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-5 sm:px-10 py-16">
        <h1 className="font-[Michroma] font-normal text-2xl sm:text-3xl uppercase mb-8" data-testid="legal-page-title">
          {title}
        </h1>
        {/* Eigene Titel-Überschrift wird immer gezeigt; eine ggf. im Snippet enthaltene h1 wird ausgeblendet,
            um doppelte Überschriften zu vermeiden (manche ITRK-Snippets liefern eine eigene h1, andere nicht). */}
        <div className="text-[#C9C9CC] [&_h1]:hidden [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-white [&_h2]:mt-8 [&_h2]:mb-3 [&_p]:mb-3 [&_p]:leading-relaxed [&_a]:text-[#E4231F]" data-testid="legal-page-body">
          <ItrkLegalText snippetUrl={snippetUrl} />
        </div>
      </main>
    </div>
  );
};

export default LegalPage;
